/**
 * Coordinates the one-way transition from an authenticated app to sign-in
 * when the API proves that its session no longer exists.
 *
 * The API client cannot import the router directly: doing so creates an import
 * cycle through the generated route tree. Instead, app startup installs this
 * small boundary handler once the router and query client are available.
 */
/**
 * How the API proved the session gone:
 * - `expired` — a token was presented and Better Auth rejected it.
 * - `missing` — no credential arrived at all. On production web that means the
 *   browser dropped the session cookie (it expired), so the tab is signed out
 *   even though the page still shows the app.
 */
export type SessionLoss = 'expired' | 'missing';

/** Return `false` to decline (e.g. this tab was never signed in). */
type SessionInvalidationHandler = (loss: SessionLoss) => boolean | undefined;

let handler: SessionInvalidationHandler | undefined;
let invalidationHandled = false;

// This exact message is emitted by AuthGuard only after Better Auth rejects a
// presented token. It is deliberately narrower than a status-only check:
// other 401s can describe an authorization failure in a downstream product
// integration and must never sign the Borradh user out.
export const INVALID_SESSION_MESSAGE = 'Invalid or expired session';

// AuthGuard's answer when a protected request carries no token or cookie at
// all — what every request sends once the browser has dropped an expired
// session cookie. Before this was recognised the user stayed on the page and
// every action failed with this raw text (Kava, 2026-10-27).
export const MISSING_SESSION_MESSAGE = 'Authentication required';

export function isInvalidSessionError(error: {
  status: number;
  message: string;
}): boolean {
  return getSessionLoss(error) === 'expired';
}

/** Which session loss a failed API response proves, if any. */
export function getSessionLoss(error: {
  status: number;
  message: string;
}): SessionLoss | undefined {
  if (error.status !== 401) return undefined;
  const message = error.message.trim();
  if (message === INVALID_SESSION_MESSAGE) return 'expired';
  if (message === MISSING_SESSION_MESSAGE) return 'missing';
  return undefined;
}

export function registerSessionInvalidationHandler(
  nextHandler: SessionInvalidationHandler
): () => void {
  handler = nextHandler;
  return () => {
    if (handler === nextHandler) handler = undefined;
  };
}

/** Invoke the app-level recovery exactly once for one lost session. */
export function handleSessionInvalidation(loss: SessionLoss = 'expired'): void {
  if (invalidationHandled) return;
  invalidationHandled = true;
  // A declined loss was not this tab's session ending, so a real one later
  // must still be able to recover.
  if (handler?.(loss) === false) invalidationHandled = false;
}

/** Allow a subsequent sign-in to install and later invalidate a new session. */
export function markSessionAuthenticated(): void {
  invalidationHandled = false;
}
